import { useId, useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { CircularProgress } from '@material-ui/core';
import { useLocation, useMatch } from 'react-router-dom';
import {
  iconsApiRef,
  useApi,
  useRouteRef,
  useTranslationRef,
} from '@backstage/frontend-plugin-api';
import { entityRouteRef } from '@backstage/plugin-catalog-react';
import {
  Button,
  Flex,
  Menu,
  MenuItem,
  MenuSection,
  MenuSeparator,
  MenuTrigger,
  Text,
  Tooltip,
  TooltipTrigger,
} from '@backstage/ui';
import { IconBox } from '../ui';
import {
  branchOptions,
  healthColor,
  type BranchOption,
} from '../projectEnvironments/argoApp';
import { BranchLabel } from '../projectEnvironments/BranchLabel';
import { compareBranches } from '../projectEnvironments/branchKind';
import {
  BRANCH_PAGE_PATH,
  branchRouteRef,
} from '../projectEnvironments/branchRouteRef';
import {
  sleepingBranches,
  usePermanentEnvironments,
} from '../projectEnvironments/permanentEnvironments';
import { useProjectAccess } from '../projectEnvironments/projectAccess';
import type { ProjectEntry } from '../projectEnvironments/projectList';
import { projectEnvironmentsTranslationRef } from '../projectEnvironments/translation';
import { waadooIcons } from '../ui/icons/waadooIcons';
import {
  healthDotColors,
  projectInitials,
  projectTone,
  type ProjectTone,
} from './compactProjects';
import { useProjectNavReads } from './useProjectNavReads';

const SleepingIcon = waadooIcons.sleeping;
const ReadOnlyIcon = waadooIcons.readOnly;

interface BadgeProps {
  project: ProjectEntry;
  initials: string;
  /** Undefined until the environments are read: no dot meanwhile. */
  tone?: ProjectTone;
  readOnly: boolean;
  active: boolean;
  /** The project's page (its catalog entity). */
  projectPath: string;
  /** Its deployed branches, from `branchOptions`; undefined until read. */
  branches?: BranchOption[];
  /** Its sleeping permanent branches, read while its menu is open. */
  sleeping: readonly string[];
  /** The branch page being read, when it is one of this project's. */
  activeBranch?: string;
  branchHref?: (branch: string) => string;
  loading: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * One project of the compact menu: its initials in a rounded square, a dot
 * for its state (`projectTone`), the eye of « Lecture seule » when the
 * reader is only a member. Pressed, a menu anchored to it: the project's
 * branches by tier — prod, the staging branches, dev, the ephemeral ones —
 * the one being read checked, then « Ouvrir le projet → ». A react-aria menu
 * (BUI): arrow keys, Escape closes it and gives the focus back to the badge.
 */
function ProjectBadge(props: BadgeProps) {
  const { project, tone, active, branches, activeBranch, branchHref } = props;
  const { t } = useTranslationRef(projectEnvironmentsTranslationRef);
  const iconsApi = useApi(iconsApiRef);
  const statusDotIcon = iconsApi.icon('status-dot');
  const checkIcon = iconsApi.icon('check');

  const name = props.readOnly
    ? t('readOnly.navProject', { project: project.label })
    : project.label;
  const label = tone
    ? t('nav.projectBadge', {
        project: name,
        state: t(`nav.projectState.${tone}`),
      })
    : name;

  // The deployed branches and the sleeping ones, in the order of every
  // branch list (`compareBranches`) — BranchSwitcher's construction.
  const entries: Array<{ branch: string; option?: BranchOption }> = [
    ...(branches ?? []).map(option => ({ branch: option.branch, option })),
    ...props.sleeping.map(branch => ({ branch })),
  ].sort((a, b) => compareBranches(a.branch, b.branch));

  // The dot inherits the surrounding text colour (see components/ui/icons/iconBundle.tsx):
  // the construction of BranchSwitcher's rows.
  const healthDot = (health: string): ReactElement | undefined =>
    statusDotIcon ? (
      <Text variant="body-small" color={healthColor(health)}>
        <IconBox size="small">{statusDotIcon}</IconBox>
      </Text>
    ) : undefined;

  let stateMark: ReactElement | null = null;
  if (tone === 'none') {
    stateMark = (
      <span className="waadoo-rail-project__state" data-tone="none">
        <SleepingIcon aria-hidden focusable="false" />
      </span>
    );
  } else if (tone) {
    stateMark = (
      <span
        className="waadoo-rail-project__state"
        data-tone={tone}
        style={{ color: healthDotColors[tone] }}
      >
        {statusDotIcon ?? null}
      </span>
    );
  }

  let placeholder: string | undefined;
  if (entries.length === 0) {
    placeholder = props.loading
      ? t('nav.loadingBranches')
      : t('nav.noBranches');
  }

  return (
    <MenuTrigger onOpenChange={props.onOpenChange}>
      <TooltipTrigger>
        <Button
          variant="tertiary"
          aria-label={label}
          className="waadoo-rail-project"
          data-active={active ? '' : undefined}
          // « page », as the expanded nav marks its open branch row: the
          // badge of the project being read.
          aria-current={active ? 'page' : undefined}
        >
          {/* Decorative, all three: the accessible name says the project,
              its state and « lecture seule ». */}
          <span className="waadoo-rail-project__initials" aria-hidden="true">
            {props.initials}
          </span>
          {stateMark}
          {props.readOnly ? (
            <span className="waadoo-rail-project__read-only" aria-hidden="true">
              <ReadOnlyIcon aria-hidden focusable="false" />
            </span>
          ) : null}
        </Button>
        <Tooltip placement="right">{project.label}</Tooltip>
      </TooltipTrigger>
      {/* Named by its trigger: react-aria labels a menu with the button
          that opens it (aria-labelledby). */}
      <Menu
        placement="right top"
        className="waadoo-rail-flyout"
        // The width of BranchSwitcher's menu, for its reason: no truncation
        // rule in bui-MenuItemContent.
        style={{ minWidth: '16rem' }}
      >
        {/* The selection is the section's: the branches are `menuitemradio`
            rows, the one being read `aria-checked`, and « Ouvrir le projet »
            stays a plain `menuitem` (SidebarUserBlock's construction). No
            onSelectionChange: the URL says which branch is read, react-aria
            never selects a link on press. */}
        <MenuSection
          title={project.label}
          selectionMode="single"
          selectedKeys={activeBranch ? [activeBranch] : []}
        >
          {entries.map(({ branch, option }) => (
            <MenuItem
              key={branch}
              id={branch}
              href={branchHref?.(branch)}
              textValue={branch}
              iconStart={option ? healthDot(option.health) : undefined}
            >
              <Flex align="center" gap="2">
                <BranchLabel
                  branch={branch}
                  timing={option}
                  sleeping={!option}
                />
                {branch === activeBranch && checkIcon && (
                  <IconBox size="small">{checkIcon}</IconBox>
                )}
              </Flex>
            </MenuItem>
          ))}
          {placeholder ? (
            <MenuItem
              id="waadoo-rail-no-branch"
              isDisabled
              textValue={placeholder}
            >
              {placeholder}
            </MenuItem>
          ) : null}
        </MenuSection>
        <MenuSeparator />
        <MenuItem
          id="waadoo-rail-open-project"
          href={props.projectPath}
          textValue={t('nav.openProject')}
        >
          {t('nav.openProject')}
          {/* Decorative: out of the item's name and typeahead text. */}
          <span aria-hidden="true"> →</span>
        </MenuItem>
      </Menu>
    </MenuTrigger>
  );
}

/**
 * The Odoo projects in the compact menu: « PROJETS », then one badge per
 * project (ProjectBadge). The environments of every project are read at
 * once — the dots need them all —, by the ONE list call the expanded menu
 * and the home page make too (`useProjectNavReads`); the sleeping branches of a
 * project only while its menu is open.
 */
export function CompactProjectsNav() {
  const { t } = useTranslationRef(projectEnvironmentsTranslationRef);
  const levelOf = useProjectAccess();
  const entityRoute = useRouteRef(entityRouteRef);
  const branchRoute = useRouteRef(branchRouteRef);
  const branchMatch = useMatch(BRANCH_PAGE_PATH);
  const { pathname } = useLocation();
  const titleId = useId();

  const { projects, projectsLoading, appsByProject, branchesLoading } =
    useProjectNavReads(branchRoute ? 'compact' : undefined);

  const [openProject, setOpenProject] = useState<string>();
  const permanent = usePermanentEnvironments(
    openProject && appsByProject ? openProject : undefined,
  );

  const initials = useMemo(() => projectInitials(projects), [projects]);

  // The reasons of the expanded block (ProjectsNav.tsx): nowhere to lead
  // without the catalog, nothing to say without a project.
  if (!entityRoute) {
    return null;
  }
  if (!projectsLoading && projects.length === 0) {
    return null;
  }

  const now = Date.now();
  return (
    <div className="waadoo-rail-projects">
      <div className="waadoo-rail-projects__title" id={titleId}>
        {t('nav.projects')}
      </div>
      {projectsLoading ? (
        <div className="waadoo-rail-row">
          <CircularProgress size={12} color="inherit" />
        </div>
      ) : (
        <ul className="waadoo-rail-projects__list" aria-labelledby={titleId}>
          {projects.map((project, index) => {
            const projectPath = entityRoute(project.params);
            const apps = appsByProject?.get(project.projectId);
            const deployed = appsByProject
              ? branchOptions(apps ?? [])
              : undefined;
            const isOpen = project.projectId === openProject;
            const routeProject = branchMatch?.params.project;
            return (
              <li key={project.entityRef}>
                <ProjectBadge
                  project={project}
                  initials={initials[index]}
                  tone={
                    appsByProject
                      ? projectTone(project, appsByProject, now)
                      : undefined
                  }
                  readOnly={levelOf(project.projectId) === 'read'}
                  active={
                    routeProject === project.projectId ||
                    pathname === projectPath ||
                    pathname.startsWith(`${projectPath}/`)
                  }
                  projectPath={projectPath}
                  branches={deployed}
                  sleeping={
                    isOpen && deployed && permanent
                      ? sleepingBranches(
                          permanent,
                          deployed.map(option => option.branch),
                        )
                      : []
                  }
                  activeBranch={
                    routeProject === project.projectId
                      ? branchMatch?.params.branch
                      : undefined
                  }
                  branchHref={
                    branchRoute
                      ? branch =>
                          branchRoute({ project: project.projectId, branch })
                      : undefined
                  }
                  // The sleeping branches are asked once the environments are
                  // listed: without that list (a failed read), nothing is.
                  loading={
                    branchesLoading ||
                    (isOpen && deployed !== undefined && !permanent)
                  }
                  onOpenChange={open =>
                    setOpenProject(current => {
                      if (open) {
                        return project.projectId;
                      }
                      return current === project.projectId
                        ? undefined
                        : current;
                    })
                  }
                />
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
